import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, catchError, mergeMap, switchMap } from 'rxjs/operators';

import { environment } from '../../../../../../environments/environment';
import { PaginableResponseModel } from '../../../../common/models/paginable-response.model';
import { Location } from '../../models/location.model';

import { LocationsService } from '../../services/locations.service';
import {
  LocationsActionTypes,
  LoadLocations,
  LoadLocationsSuccess,
  LoadLocationsFailure,
  LoadLocation,
  LoadLocationSuccess,
  LoadLocationFailure,
  CreateLocation,
  CreateLocationSuccess,
  CreateLocationFailure,
  UpdateLocation,
  UpdateLocationSuccess,
  UpdateLocationFailure,
  DeleteLocation,
  DeleteLocationSuccess,
  DeleteLocationFailure
} from '../actions/locations.actions';

@Injectable()
export class LocationsEffects {
  constructor(
    private actions$: Actions,
    private service: LocationsService
  ) { }

  
  loadLocations$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(LocationsActionTypes.LoadLocations),
    mergeMap((action: LoadLocations) =>
      this.service.getBySearch(action.payload.filter).pipe(
        map((res: PaginableResponseModel<Location>) => {
          if (!res || !res.isSuccess) {
            return new LoadLocationsFailure({ 
              error: 'No se pudo cargar la lista de centros de trabajo' 
            });
          }

          const itemsWithUrl = (res.values || []).map(item => ({
            ...item,
            publicUrl: this.buildPublicUrl(item)
          }));

          return new LoadLocationsSuccess({
            items: itemsWithUrl,
            total: res.total
          });
        }),
        catchError(error =>
          of(new LoadLocationsFailure({ error }))
        )
      )
    )
  ));

  private buildPublicUrl(location: any): string | null {
    const merchantSlug = location.merchantSlug;
    const locationSlug = location.slug;

    if (!merchantSlug || !locationSlug) {
      return null;
    }

    return `${environment.bookingUrl}/${merchantSlug}/${locationSlug}`;
  }

  
  loadLocationByUid$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(LocationsActionTypes.LoadLocation),
    mergeMap((action: LoadLocation) =>
      this.service.getById(action.payload.id).pipe(
        map(response => {
          if (!response || !response.isSuccess) {
            return new LoadLocationFailure({ 
              error: 'No se pudo cargar el centro de trabajo' 
            });
          }
          return new LoadLocationSuccess({ location: response.value });
        }),
        catchError(error => 
          of(new LoadLocationFailure({ error }))
        )
      )
    )
  ));

  
  createLocation$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(LocationsActionTypes.CreateLocation),
    mergeMap((action: CreateLocation) =>
      this.service.create(action.payload.location).pipe(
        map(response => {
          if (!response || !response.isSuccess) {
            return new CreateLocationFailure({ 
              error: 'No se pudo crear el centro de trabajo' 
            });
          }
          return new CreateLocationSuccess({ location: response.value });
        }),
        catchError(error => 
          of(new CreateLocationFailure({ error }))
        )
      )
    )
  ));

  
  updateLocation$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(LocationsActionTypes.UpdateLocation),
    mergeMap((action: UpdateLocation) =>
      this.service.update(action.payload.location).pipe(
        map(response => {
          if (!response || !response.isSuccess) {
            return new UpdateLocationFailure({
              error: 'No se pudo actualizar el centro de trabajo'
            });
          }
          return new UpdateLocationSuccess({ location: response.value });
        }),
        catchError(error =>
          of(new UpdateLocationFailure({ error }))
        )
      )
    )
  ));


  deleteLocation$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(LocationsActionTypes.DeleteLocation),
    mergeMap((action: DeleteLocation) =>
      this.service.delete(action.payload.id).pipe(
        map(response => {
          if (!response || !response.isSuccess) {
            return new DeleteLocationFailure({
              error: 'No se pudo eliminar el centro de trabajo'
            });
          }
          const cancelled = response.value && response.value.cancelledBookingsCount
            ? response.value.cancelledBookingsCount
            : 0;
          return new DeleteLocationSuccess({
            id: action.payload.id,
            cancelledBookingsCount: cancelled,
            filter: action.payload.filter
          });
        }),
        catchError(error =>
          of(new DeleteLocationFailure({ error }))
        )
      )
    )
  ));

}
